import type { BanDich } from '../../types';
import type { viKetNoi } from './ket-noi.vi';

export const enKetNoi: Pick<BanDich, keyof typeof viKetNoi> = {
  troLyAiKetNoi: {
    tieuDe: 'Data source connections',
    phuDe: 'The data systems the assistant may read — directly or through a periodically refreshed copy, always with a read-only account.',
    taiLieuKetNoi: 'Setup guides',
    taiLieuDsn: 'Database (DSN) connection guide — for the database admin',
    taiLieuGraphql: 'GraphQL connection guide — for the operator',
    taiLieuChuanVao: 'GraphQL input specification — for the team building the endpoint',
    themCsdl: 'Add database (DSN)',
    themGraphqlNut: 'Add GraphQL',
    themApiNut: 'Add API',
    rongTieuDe: 'No sources connected yet',
    rongMoTa: 'Use DSN when the customer only has a database and a read-only account; use GraphQL when the customer has a development team that builds an endpoint.',
    themDauTien: 'Add the first connection',
    danhSach: 'Connected data sources',
    noiDuoc: 'Connected · {{n}} tables readable',
    noiDuocChiThuTuc: 'Connected · source only allows stored procedures · {{n}} procedures callable',
    khongNoiDuoc: "Couldn't connect — {{loi}}",
    daDungKho: 'Replica store created — {{ten}}',
    khongDungDuocKho: "Couldn't create the replica store — {{loi}}",
    dongBoBatDau: 'Refreshing {{n}} tables in the background — follow progress in Data store',
    khongDongBoDuoc: "Couldn't refresh the data — {{loi}}",
    khongDoiDuoc: "Couldn't change it — {{loi}}",
    daDoiNhip: 'Sync interval changed.',
    daDoiMoTa: 'Source description saved.',
    daDoiCheDo: 'Data access mode changed.',
    daBoVaXoa: 'Source and data store deleted.',
    daBoGiuKho: 'Source deleted — the data store was kept.',
    khongXoaDuoc: "Couldn't delete — {{loi}}",
    daXoaKhoCu: 'Old replica {{ten}} cleaned up.',
    khongXoaDuocKhoCu: "Couldn't clean up the old replica — {{loi}}",
    xacNhanBo: 'Delete source “{{ten}}”?',
    xacNhanBoKhongKho: "This source has no store of its own. Its business declarations, permissions and query templates are deleted with it.",
    xacNhanXoaKho: 'Delete the source and database {{ten}}?',
    xacNhanXoaKhoGhi: "This can't be undone. All synced data is deleted along with the source.",
    boNguon: 'Delete source',
    xoaCaKho: 'Delete source and store',
    xacNhanDoiCheDo: 'Change how “{{ten}}” is read?',
    xacNhanSangSaoChep: 'Questions will run on a copy stored in the platform. After switching, click “Create replica store” and then “Refresh data” before you can ask.',
    xacNhanSangTrucTiep: "From now on questions read the customer's database directly and scheduled sync is turned off. The old replica (if any) isn't deleted — clean it up with “Clean up old replica” in the card's ⋯ menu once you're sure you won't switch back.",
    doiCheDo: 'Change access mode',
    xacNhanXoaKhoCu: 'Clean up the old replica {{ten}}?',
    xacNhanXoaKhoCuGhi: "Frees the space used by synced data. The source, business declarations, permissions and column statistics stay — the assistant can still query directly. This can't be undone: switching back to copy mode means creating the store and syncing from scratch.",
    xoaKhoCu: 'Clean up old replica',
    khaiNhanh: 'Quick setup from template',
    khaiNhanhTieuDe: 'Quick setup for “{{ten}}”',
    khaiNhanhSapGhi: "This was recognized as SAP Business One. The assistant will be given the content below about this source — how to read SAP's tables and how to interpret Vietnamese accounting figures. It goes with every question on this source, so read it first. You can edit it later in Data store → Notes & glossary.",
    khaiNhanhThuongGhi: 'No sign of SAP Business One (no jdt1, ojdt, oact tables), so only the Vietnamese accounting part is provided. The assistant will be given the content below with every question on this source — read it first. You can edit it later in Data store → Notes & glossary.',
    khaiNhanhKiem: 'Four queries to run after setup (not written to the declaration)',
    khaiNhanhGhi: 'Write to business notes',
    khaiNhanhDangDo: "Reading the source's table list...",
    daKhaiNhanh: "Done. The assistant now knows how to interpret this source's accounting figures — review and adjust it to your business in Data store → Notes & glossary.",
    daKhaiNhanhSap: "Done. The assistant now knows how to read SAP tables and interpret this source's accounting figures. Four points still need checking — see Data store → Notes & glossary.",
    khaiNhanhHong: "Couldn't set it up — {{loi}}",
    khaiNhanhQuaDai: "This source's notes would be {{tong}} characters long, over the {{tran}} limit. Shorten the existing notes in Data store → Notes & glossary, then try again.",
    biMatTieuDe: 'Webhook secret — shown only once',
    biMatGhi: "Send this to the source's technical team. Their system calls the address below whenever there's new data; the platform records it as a notification so an operator can sync right away, or lets the schedule run.",
    biMatCanhBao: "The platform only keeps a reference to this secret, so it can't be shown again. If it's lost, reset it through the API.",
    toiDaLuu: "I've saved it",
    chepKhoa: 'Copy request',
    daChep: 'Copied to clipboard.',
  },
  troLyAiKetNoiThe: {
    trangThai: {
      draft: 'Not checked',
      connected: 'Connected',
      ready: 'Ready',
      error: 'Connection error',
    },
    loaiGraphql: 'GraphQL',
    loaiApi: 'API',
    nhanTrucTiep: 'Direct read',
    nhanSaoChep: 'Periodic copy',
    cachDocTrucTiep: 'every question reads the latest data',
    cachDocSaoChepTuDong: 'refreshes every {{n}} minutes',
    cachDocSaoChepTay: 'refreshes on demand',
    cachDocApi: 'every question calls the business API',
    traLoiTheoKhuon: 'Answers from query templates',
    traLoiTheoBanKhai: 'Answers from business declarations',
    themMoTa: '+ Add a business description',
    xemSoDo: 'View table diagram',
    suaKetNoiApi: 'Edit API connection',
    chuyenSangSaoChep: 'Switch to periodic copy',
    chuyenSangTrucTiep: 'Switch to direct read',
    chiTietKetNoi: 'Connection details',
    ctDiaChi: 'Address',
    ctKhoBanSao: 'Replica store',
    ctVaiDoc: 'Read-only account',
    ctXacThuc: 'Authentication',
    moTaNguon: 'Source description',
    moTaNguonGoiY: 'Business description, e.g. Clothing sales system, Hanoi branch',
    moTaNguonGhiChu: 'Used as the heading for this source when the assistant answers from several sources. Leave blank to use the source name.',
    bangDocDuoc: '{{n}} tables readable',
    thuTucGoiDuoc: '{{n}} procedures callable',
    chiThuTucSanSang: 'This source only allows stored procedures — answered through query templates.',
    chiThuTucCanChuyen: 'This source only allows stored procedures; there are no tables for the assistant to write queries against. Open Query templates and switch to template declarations before Q&A can use this source.',
    luocDo: 'schema',
    luocDoKhac: 'Other schemas on this server: {{ds}}',
    conKhoCu: 'An old data replica is no longer used — you can clean it up from the ⋯ menu to free space.',
    tuDongBo: 'Scheduled refresh',
    tuDongBoMoiTruoc: 'Refresh every',
    tuDongBoMoiSau: 'minutes',
    nhipNhan: 'Minutes between automatic refreshes',
    dongBoThuCong: 'Scheduled refresh (off)',
    kiemNoi: 'Test connection',
    moKhuon: 'Declare query templates',
    moKhoDuLieu: 'Open data store',
    dungKho: 'Create replica store',
    dongBo: 'Refresh data',
  },
  troLyAiKetNoiThem: {
    themDsn: 'Add database connection (DSN)',
    themGraphql: 'Add GraphQL source',
    themApi: 'Add API source',
    themNguon: 'Add source',
    daKhaiNguon: 'Source added — click “Test connection” to see what the account can read.',
    khongTaoDuoc: "Couldn't create it — {{loi}}",
    tenHienThi: 'Display name',
    tenViDu: 'Financial reports 2026',
    maNguon: 'Source code',
    maNguonChuaCo: 'Generated from the name — enter a name first.',
    maNguonGhi: "Generated from the name. It's used in the replica database name, so it can't change once the store is created.",
    cheDoLabel: 'Data access mode',
    cheDoTrucTiep: 'Query directly (recommended)',
    cheDoSaoChep: 'Copy periodically to a separate store',
    cheDoTrucTiepGhi: "Every question reads this database directly — the platform only stores the schema, business declarations and column statistics, never data rows. Suitable when the customer's database is already a replica and can handle the query load.",
    cheDoSaoChepGhi: "Data is synced to a separate store on a schedule and questions run on the copy. Use it when the customer's database has limited resources and shouldn't take reporting queries.",
    loaiCsdlLabel: 'Database type',
    luocDoLabel: 'Schema',
    luocDoGhi: 'Leave blank for the default.',
    mayChu: 'Host',
    cong: 'Port',
    congGhi: 'Leave blank for the default.',
    tenCsdl: 'Database name',
    taiKhoan: 'Username',
    matKhau: 'Password',
    hienMatKhau: 'Show',
    anMatKhau: 'Hide',
    hienMatKhauNhan: 'Show password',
    anMatKhauNhan: 'Hide password',
    danDsn: 'Or paste a connection string (DSN)',
    dsnGhi: 'If you already have a connection string, paste it here and the fields above fill in. The password is split off and moved to the secret store as soon as you save — afterwards only user:***@host/db is shown.',
    xinQuyen: 'Ask the database admin for a read-only account:',
    mssqlCanhBao: "SQL Server has no session-level read-only switch, so the account's permissions are the only line of defense.",
    pgGhi: "The platform also turns on default_transaction_read_only at the server level — but that's only the second line of defense; the account's permissions on the customer side are the main one.",
    endpoint: 'GraphQL endpoint',
    khoaApi: 'API key (optional)',
    khoaApiGhi: 'Sent as Authorization: Bearer. Kept in the secret store and never shown again.',
    dongBoTheoLich: 'Sync on a schedule',
    moiPhut: 'Every (minutes)',
    baBuocGhi: 'Go through three steps in order: declare, test the connection, create the store. They are separate because each step fails for different reasons and is fixed differently.',
    baBuocGhiTrucTiep: 'After declaring, click “Test connection” — once it connects you can ask right away, no store needed.',
    baBuocGhiApi: 'After declaring, click “Test connection”, then go to Query templates to declare each API.',
    apiDiaChiGoc: 'API base URL',
    apiDiaChiGocGhi: "The part shared by every business API, e.g. https://erp.khach.vn/api. Each operation's own path is declared in its query template.",
    apiDiaChiSai: 'The address must start with http:// or https://.',
    apiXacThuc: 'Authentication',
    apiXacThucNone: 'No key needed',
    apiXacThucBearer: 'Bearer token',
    apiXacThucApiKey: 'API key',
    apiXacThucBasic: 'Username + password (Basic)',
    apiTenKhoa: 'Header carrying the key',
    apiViTriKhoa: 'Send the key via',
    apiViTriHeader: 'Header',
    apiViTriQuery: 'URL parameter',
    apiTenDangNhap: 'Username',
    apiKhoa: 'Key / password',
    apiKhoaGhi: "Kept in the secret store and never shown again. Every query template of this source uses this key.",
    apiKhoaGiuNguyen: 'Leave blank to keep the saved key. A saved key is never shown again.',
    apiThoiGianCho: 'Timeout (seconds)',
    apiThoiGianChoGhi: 'Reporting APIs over large data are often slow. At most 60 seconds.',
    apiHopDongTieuDe: 'The business API must return exactly this shape',
    apiHopDongGhi: 'The API filters, sums and enforces data permissions itself — like a stored procedure. The platform only declares, calls and presents. Business errors return a 4xx status with a message field.',
    suaKetNoiApi: 'Edit API connection',
    luuKetNoiApi: 'Save connection',
    daLuuKetNoiApi: 'Connection saved. Click “Test connection” to try again.',
  },
  troLyAiKetNoiSoDo: {
    tieuDe: 'Data diagram — {{ten}}',
    changNguonDsn: 'Customer database',
    changNguonGraphql: 'GraphQL endpoint',
    luocDoLa: 'schema {{luocDo}}',
    changDongBo: 'Sync',
    moiPhut: 'runs every {{n}} minutes',
    thuCong: 'runs manually',
    soLieuToi: 'data up to {{luc}}',
    chuaDongBo: 'never synced',
    changKho: 'Replica store',
    chuaDungKho: 'no store yet',
    khoGhi: '{{n}} tables · {{dong}} rows',
    changVaiDoc: 'Read-only account',
    vaiDocGhi: 'model-generated SQL runs under this account',
    changTroLy: 'AI Assistant',
    sinhSql: 'generates SQL from the question',
    troLyGhi: 'AI Q&A · Chat with a bot',
    altSoDo: 'Relationship diagram: {{bang}} tables, {{canh}} relationships',
    chuThichFk: 'foreign key',
    chuThichChieu: 'shared dimension',
    chuThichNhan: '1 / 0..* are the cardinalities at each end',
    phongTo: 'Zoom in',
    thuNho: 'Zoom out',
    vuaKhung: 'Fit to view',
    congCuTay: "Pan mode — clicking doesn't select a table",
    rongMoTa: "This source hasn't declared any tables. Test the connection, then confirm the schema in Data store.",
    loiTieuDe: "Couldn't read the source's declaration",
    loiMoTa: 'Try again, or check that the source still exists.',
    khoVaLuocDo: '{{kho}} · schema {{luocDo}}',
    chayDongChay: 'Simulate JOIN paths',
    dung: 'Stop',
    duongJoinCua: 'JOIN paths of {{ten}}',
    moiDuongJoin: 'all JOIN paths',
    demBangQuanHe: '{{bang}} tables · {{canh}} relationships',
    ghiChayDongChay: "This illustrates the path of a JOIN, not a sync flow — two tables in the replica don't exchange data. Click a table to see only its paths.",
    chuaKhaiQuanHeTieuDe: 'No relationships declared yet.',
    chuaKhaiQuanHeMo: "The replica has no foreign keys, so this is the only place that tells the model which tables it may JOIN. Without it, any question that needs two tables can't be answered. Run “Detect codes & relationships” in Data store.",
    boChon: 'Clear selection',
    motDongLa: 'one row = {{grain}}',
    soMa: '{{n}} codes',
    soCot: '{{n}} columns',
    soNoi: '{{n}} links',
    chuaNoi: 'not linked',
    locBang: 'Filter tables...',
    khongKhop: 'No table matches “{{loc}}”.',
    danhSachBang: 'Table list',
  },
};
